import type { Dispatch, FC, FormEvent, ReactNode, SetStateAction } from 'react'
import { useLingui } from '@lingui/react/macro'
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js'
import type {
  StripeCardNumberElement,
  StripeElementChangeEvent,
  StripeElements,
  StripeElementType,
} from '@stripe/stripe-js'

import { cn } from '@jotta/utils/css'

import { Label } from '../../Forms/Label/Label'

export type StripeValidationError = {
  [key in StripeElementType]?:
    | undefined
    | {
        type: 'validation_error'
        code: string
        message: string
      }
}

export interface StripeCardFormProps {
  /** Verification handler  */
  onVerify: (cardElement: StripeCardNumberElement | null) => void
  errors: StripeValidationError
  elements: StripeElements | null
  setErrors: Dispatch<SetStateAction<StripeValidationError>>
  children?: ReactNode
  className?: string
}

export const StripeCardForm: FC<StripeCardFormProps> = ({
  onVerify,
  errors,
  setErrors,
  elements,
  children,
  className,
}) => {
  const { t } = useLingui()

  const handleSubmit = (event?: FormEvent) => {
    event?.preventDefault()
    if (elements) {
      onVerify(elements.getElement(CardNumberElement))
    }
  }

  const errorHandler = ({ elementType, error }: StripeElementChangeEvent) =>
    setErrors(prevState => ({ ...prevState, ...{ [elementType]: error } }))

  const errorMessage = (field: StripeElementType) => errors[field]?.message

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('grid w-full grid-cols-2 gap-3', className)}
    >
      <Label
        className="col-span-full"
        label={t`Card number`}
        errorMessage={errorMessage('cardNumber')}
      >
        <CardNumberElement
          id="card-number"
          onChange={errorHandler}
          options={{
            placeholder: '0000 0000 0000 0000',
            classes: {
              base: 'input input-stripe bg-surface-container-lowest',
              invalid: 'input-error',
              focus: 'input-focus',
            },
          }}
        />
      </Label>
      <Label label={t`Expiry date`} errorMessage={errorMessage('cardExpiry')}>
        <CardExpiryElement
          className="input input-stripe bg-surface-container-lowest"
          id="exp-date"
          onChange={errorHandler}
          options={{
            classes: {
              base: 'input input-stripe',
              invalid: 'input-error',
              focus: 'input-focus',
            },
          }}
        />
      </Label>
      <Label label={t`CVC`} errorMessage={errorMessage('cardCvc')}>
        <CardCvcElement
          id="cvc"
          className="input input-stripe bg-surface-container-lowest"
          onChange={errorHandler}
          options={{
            placeholder: '000',
            classes: {
              base: 'input input-stripe',
              invalid: 'input-error',
              focus: 'input-focus',
            },
          }}
        />
      </Label>
      {children}
    </form>
  )
}
